// src/components/Target/SetTargetModal.jsx
import { useState, useEffect } from 'react';
import targetService from '../../services/target.service';
import Toast from '../Toast/Toast';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const fmt = (a) => `₹${Number(a||0).toLocaleString('en-IN', { minimumFractionDigits:2 })}`;

const SetTargetModal = ({ onClose, onSuccess }) => {
  const now = new Date();

  const [users,        setUsers]        = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast,        setToast]        = useState(null);
  const [formData, setFormData] = useState({
    userId:       '',
    targetAmount: '',
    month:        now.getMonth() + 1,
    year:         now.getFullYear(),
    notes:        '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    targetService.getAssignableUsers()
      .then(res => { if (res.success) setUsers(res.data || []); })
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.userId)       e.userId       = 'Select a team member';
    if (!formData.targetAmount || parseFloat(formData.targetAmount) <= 0)
                                e.targetAmount = 'Enter valid target amount';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const res = await targetService.setTarget({
        userId:       parseInt(formData.userId),
        targetAmount: parseFloat(formData.targetAmount),
        month:        parseInt(formData.month),
        year:         parseInt(formData.year),
        notes:        formData.notes || undefined,
      });
      if (res.success) {
        setToast({ message: 'Target set successfully!', type: 'success' });
        setTimeout(() => { onClose(); onSuccess?.(); }, 1000);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to set target', type: 'error' });
    } finally { setIsSubmitting(false); }
  };

  // Group users by role
  const grouped = users.reduce((acc, u) => {
    const role = u.role?.displayName || u.role?.name || 'Other';
    if (!acc[role]) acc[role] = [];
    acc[role].push(u);
    return acc;
  }, {});

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.45)',
      zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20
    }} onClick={onClose}>
      <div style={{
        background:'#fff', borderRadius:14, width:'100%', maxWidth:480,
        boxShadow:'0 12px 48px rgba(0,0,0,0.18)', maxHeight:'90vh',
        display:'flex', flexDirection:'column',
      }} onClick={e => e.stopPropagation()}>

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #e9ecef', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h5 style={{ margin:0, fontWeight:700, fontSize:16 }}>🎯 Set Monthly Target</h5>
            <p style={{ margin:'2px 0 0 0', fontSize:12, color:'#6c757d' }}>Assign sales target to a team member</p>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:18, color:'#6c757d', cursor:'pointer' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding:'20px 24px', overflowY:'auto', flex:1 }}>

          {/* Month + Year */}
          <div style={{ display:'flex', gap:12, marginBottom:16 }}>
            <div style={{ flex:1 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#495057', display:'block', marginBottom:5 }}>
                Month <span style={{ color:'#dc3545' }}>*</span>
              </label>
              <select name="month" value={formData.month} onChange={handleChange}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, outline:'none' }}>
                {MONTHS.map((m, i) => (
                  <option key={i+1} value={i+1}>{m}</option>
                ))}
              </select>
            </div>
            <div style={{ flex:1 }}>
              <label style={{ fontSize:12, fontWeight:600, color:'#495057', display:'block', marginBottom:5 }}>
                Year <span style={{ color:'#dc3545' }}>*</span>
              </label>
              <select name="year" value={formData.year} onChange={handleChange}
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, outline:'none' }}>
                {[2025, 2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Team member */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#495057', display:'block', marginBottom:5 }}>
              Team Member <span style={{ color:'#dc3545' }}>*</span>
            </label>
            {loadingUsers ? (
              <p style={{ fontSize:13, color:'#adb5bd' }}>Loading team members...</p>
            ) : users.length === 0 ? (
              <p style={{ fontSize:13, color:'#e53935' }}>No team members available to assign targets</p>
            ) : (
              <select name="userId" value={formData.userId} onChange={handleChange}
                style={{ width:'100%', padding:'9px 12px', border:`1px solid ${errors.userId?'#dc3545':'#dee2e6'}`, borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="">Select team member</option>
                {Object.entries(grouped).map(([role, members]) => (
                  <optgroup key={role} label={role}>
                    {members.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} ({u.employeeCode || u.username})
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
            {errors.userId && <span style={{ fontSize:11, color:'#dc3545' }}>{errors.userId}</span>}
          </div>

          {/* Target Amount */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, fontWeight:600, color:'#495057', display:'block', marginBottom:5 }}>
              Target Amount (₹) <span style={{ color:'#dc3545' }}>*</span>
            </label>
            <input type="number" name="targetAmount" value={formData.targetAmount} onChange={handleChange}
              placeholder="e.g. 100000"
              style={{ width:'100%', padding:'9px 12px', border:`1px solid ${errors.targetAmount?'#dc3545':'#dee2e6'}`, borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}/>
            {formData.targetAmount && !errors.targetAmount && (
              <p style={{ fontSize:12, color:'#2e7d32', margin:'4px 0 0 0', fontWeight:600 }}>
                {fmt(formData.targetAmount)}
              </p>
            )}
            {errors.targetAmount && <span style={{ fontSize:11, color:'#dc3545' }}>{errors.targetAmount}</span>}
          </div>

          {/* Notes */}
          <div>
            <label style={{ fontSize:12, fontWeight:600, color:'#495057', display:'block', marginBottom:5 }}>Notes (optional)</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange}
              placeholder="Any additional notes for this target..."
              rows={2} style={{ width:'100%', padding:'9px 12px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box', outline:'none' }}/>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:'1px solid #e9ecef', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} disabled={isSubmitting}
            style={{ padding:'9px 20px', background:'#f8f9fa', color:'#495057', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting || loadingUsers || users.length === 0}
            style={{ padding:'9px 20px', background:'#4caf50', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', opacity: (isSubmitting || users.length === 0) ? 0.6 : 1 }}>
            {isSubmitting ? 'Setting...' : '🎯 Set Target'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetTargetModal;